import { useContext, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { FeedbackContext, MetrcKeyContext } from '../../App';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import InputBase from '@mui/material/InputBase';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/system';

import { DownArrowIcon, InfoIcon, WhatsThisIcon } from '../utility/CustomSvgIcons';
import OrgOutlinePage from './OrgSetupOutline';

import {
  defaultNoAuthRoute,
  errFeedbackFn,
  farmsWizardRoute,
  getHandleErrorWithFeedbackCX,
  landingPageRoute,
  metrcVersion,
  miscClasses,
  orgSetupClasses,
  states,
} from '../utility/constants';

import APIService from '../../api/apiService';
import useThemeVal from '../../utility/useThemeVal';
import getTokenDetails from '../../auth/getTokenDetails';
import DownloadingMetrcDataMsg from '../utility/DownloadingMetrcDataMsg';

const totalStepCount = 2;

const orgKeys = [
  { key: 'org_name', otherProps: { label: 'Organization Name' } },
  { key: 'address', otherProps: { label: 'Address' } },
];

const complianceKeys = [
  { key: 'metrc_id', otherProps: { label: 'Metrc Key' } },
  { key: 'state', otherProps: { label: 'State', select: true, SelectProps: { IconComponent: DownArrowIcon } } },
  { key: 'environment_type', otherProps: { label: 'Environment', select: true, SelectProps: { IconComponent: DownArrowIcon } } },
  { key: 'version', otherProps: { label: 'Metrc Version', select: true, SelectProps: { IconComponent: DownArrowIcon } } },
];

const environmentOptions = [
  { code: 1, name: 'Production' },
  { code: 2, name: 'Sandbox' },
];

const agreeInfo = 'I agree to Terms and Conditions.';
const allowInfo = 'This allows us to make connection with your compliance organization on your behalf and download data for further processing.';

const ORG_DETAILS = 1;
const METRC_DETAILS = 2;

export default function OrganizationSetup(props) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({});

  const feebackCX = useContext(FeedbackContext);

  const { metrcKey, setMetrcKey } = useContext(MetrcKeyContext);

  const navigate = useNavigate();

  const currDarkThemeClass = useThemeVal(theme);

  const { token, tokenDetails } = getTokenDetails();

  const { farmSetup, farmCount = 0 } = tokenDetails;

  const handleSetValues = (prop, value) => setValues(state => ({ ...state, [prop]: value }));

  const handleError = err => {
    setLoading(false);
    console.log('an error occured');
    console.log(err);
    // enable error feedback messages
    getHandleErrorWithFeedbackCX(feebackCX)(errFeedbackFn({}))(err);
    navigate('/');
  };

  const handleOrgSetupRes = res => {
    setLoading(false);

    // Generate a new token after org setup and expire the old one
    // Then reset the localStorage.token in here

    const {
      data: { token },
    } = res;

    handleSetValues('step', 2);
    handleSetValues('stepDetail', 'Metrc Details');
    handleSetValues('title', 'Please provide your Metrc credentials');
    handleSetValues('agree', false);

    localStorage.setItem('token', token);
    // sessionStorage.setItem('orgSetup', true);
  };

  const handleProcessingFarmsRes = timerId => res => {
    if (res.status === 202) return;
    // console.log('Farm Res Processed');console.log(res);
    clearInterval(timerId);

    const {
      data: { token },
    } = res;

    // new token with farmSetup === true;
    localStorage.setItem('token', token);

    // navigate('/farms?tab=farm-name', {replace: true});
    navigate(farmsWizardRoute, { replace: true });
  };

  const handleFarmDetailRes = res => {
    if (res.status === 202) {
      const timerId = setInterval(() => {
        handleSetValues('timerId', timerId);

        const entity = 'farm/?token=true';

        APIService.fetchInstance(entity).then(handleProcessingFarmsRes(timerId)).catch(handleError);
      }, 15000);

      return;
    }

    const {
      data: { token },
    } = res;

    localStorage.setItem('token', token);

    navigate(farmsWizardRoute, { replace: true });
  };

  const handleMetrcSetupRes = res => {
    setLoading(false);

    const {
      data: { metrc_id, token },
    } = res;

    setMetrcKey(metrc_id);

    handleSetValues('downloading', true);
    handleSetValues('metrc_id', metrc_id);

    localStorage.setItem('token', token);

    const entity = `farm/`;

    const payload = {
      data_fetch: true,
    };

    APIService.createInstance(entity, payload).then(handleFarmDetailRes).catch(handleError);
  };

  useEffect(() => {
    if (!token || farmCount > 0) return;

    let newTokenDetails = {};

    try {
      newTokenDetails = JSON.parse(atob(token?.split('.')?.[1]));
    } catch {
      localStorage.clear();
      sessionStorage.clear();

      return <Navigate to={defaultNoAuthRoute} />;
    }

    const { metrcSetup, orgSetup, metrc_id } = newTokenDetails;

    if (metrcSetup && orgSetup) {
      if (!metrcKey) {
        setMetrcKey(metrc_id);
      }

      setValues({
        downloading: true,
      });

      let entity = `farm/`;

      if (farmCount === 0) {
        entity += '?token=true';
      }

      APIService.fetchInstance(entity).then(handleFarmDetailRes).catch(handleError);
    } else if (orgSetup) {
      setValues({
        agree: false,
        step: 2,
        stepDetail: 'Metrc Details',
        title: 'Please provide your Metrc credentials',
      });
    } else {
      setValues({
        step: 1,
        stepDetail: 'Organization Details',
        title: 'Tell us about your Organization',
      });
    }

    return () => clearInterval(values?.timerId);
  }, []);

  useEffect(() => {
    if (!token) {
      navigate(defaultNoAuthRoute);
    } else if (farmCount > 0) {
      navigate(landingPageRoute);
    }
  }, [token, farmCount]);

  if (!token || farmCount > 0) {
    return <></>;
  }

  const oSOrgOutlineProps = {
    currDarkThemeClass,
  };

  const oSBoxProps = {
    className: `${orgSetupClasses?.box} ${currDarkThemeClass}`,
  };

  const oSStepTypoProps = {
    className: `${orgSetupClasses?.stepTypo} ${currDarkThemeClass}`,
  };

  const oSProgressBoxProps = {
    className: `${orgSetupClasses?.progressBox} ${currDarkThemeClass}`,
  };

  const oSProgressElemBoxProps = i => ({
    className: `${orgSetupClasses?.progressElemBox} ${currDarkThemeClass} ${i < (+values?.step ?? 1) ? orgSetupClasses?.completedStep : ''}`,
  });

  const progressList = Array.from({ length: totalStepCount ?? 2 }).map((e, i) => <Box key={i} {...oSProgressElemBoxProps(i)}></Box>);

  const oSStepDetailTypoProps = {
    className: `${orgSetupClasses?.detailTypo} ${currDarkThemeClass}`,
  };

  const oSActionTypoProps = {
    className: `${orgSetupClasses?.actionTypo} ${currDarkThemeClass}`,
  };

  const handleSubmit = event => {
    event.preventDefault();

    switch (+values?.step) {
      case ORG_DETAILS:
        const orgKeys = ['org_name', 'address'];

        if (orgKeys.some(key => !values?.[key])) return;

        setLoading(true);

        const orgKeyValuePairs = orgKeys.map(key => [key, values[key]]);

        const orgEntity = 'organization/';
        const orgPayload = Object.fromEntries(orgKeyValuePairs);

        APIService.createInstance(orgEntity, orgPayload).then(handleOrgSetupRes).catch(handleError);

        break;

      case METRC_DETAILS:
        const metrcKeys = ['agree', 'metrc_id', 'state', 'environment_type', 'version'];

        if (metrcKeys.some(key => !values?.[key])) return;

        setLoading(true);

        const metrcEntity = 'metrc-key/';

        const metrcKeyValuePairs = metrcKeys.map(key => [key, values[key]]);
        const metrcPayload = Object.fromEntries(metrcKeyValuePairs);

        APIService.createInstance(metrcEntity, metrcPayload).then(handleMetrcSetupRes).catch(handleError);

        break;

      default:
        break;
    }
  };

  const oSFormBoxProps = {
    component: 'form',
    onSubmit: handleSubmit,
    className: `${orgSetupClasses?.formBox} ${currDarkThemeClass}`,
  };

  const oSWhatsThisIconProps = {
    className: `${orgSetupClasses?.whatsThisIcon} ${currDarkThemeClass}`,
  };

  const oSTextFieldProps = (prop, otherProps = {}) => ({
    autoComplete: 'off',
    autoFocus: prop === 'org_name' || prop === 'metrc_id',
    className: `${orgSetupClasses?.textField} ${currDarkThemeClass}`,
    disabled: loading,
    InputProps: {
      endAdornment: prop === 'metrc_id' && <WhatsThisIcon {...oSWhatsThisIconProps} />,
    },
    label: ' ',
    onChange: ({ target: { value } }) => handleSetValues(prop, value),
    required: true,
    value: values?.[prop] ?? '',
    ...otherProps,
  });

  const OrganizationForm = orgKeys.map((e, i) => <TextField key={i} {...oSTextFieldProps(e.key, e.otherProps)} />);

  const oSAgreeBoxProps = {
    className: `${orgSetupClasses?.agreeBox} ${currDarkThemeClass}`,
  };

  const oSCheckboxProps = {
    className: `${orgSetupClasses?.checkbox} ${currDarkThemeClass}`,
    disabled: loading,
    onChange: ({ target: { checked } }) => handleSetValues('agree', checked),
    required: true,
    type: 'checkbox',
    value: values?.agree ?? false,
  };

  const oSAgreeTypoProps = {
    className: `${orgSetupClasses?.agreeTypo} ${currDarkThemeClass}`,
  };

  const oSAllowInfoBoxProps = {
    className: `${orgSetupClasses?.allowInfoBox} ${currDarkThemeClass}`,
  };

  const oSAllowInfoIconProps = {
    className: `${orgSetupClasses?.allowInfoIcon} ${currDarkThemeClass}`,
  };

  const oSAllowInfoTypoProps = {
    className: `${orgSetupClasses?.allowInfoTypo} ${currDarkThemeClass}`,
  };

  const oSStateMenuItemProps = {
    className: `${orgSetupClasses?.stateMenuItem} ${currDarkThemeClass}`,
    focusVisibleClassName: miscClasses?.menuItemFocusDark,
  };

  const stateList = states.map((e, i) => (
    <MenuItem key={i} value={e.id} {...oSStateMenuItemProps}>
      {e.name}
    </MenuItem>
  ));

  const environmentList = environmentOptions.map((env, i) => (
    <MenuItem key={i} value={env.code} {...oSStateMenuItemProps}>
      {env.name}
    </MenuItem>
  ));

  const metrcVersionList = metrcVersion.map((v, i) => (
    <MenuItem key={v.id} value={v.name} {...oSStateMenuItemProps}>
      {v.name}
    </MenuItem>
  ));

  const ComplianceForm = (
    <>
      {complianceKeys.map((e, i) => (
        <TextField key={i} {...oSTextFieldProps(e.key, e.otherProps)}>
          {e.key === 'state' && stateList}
          {e.key === 'environment_type' && environmentList}
          {e.key === 'version' && metrcVersionList}
        </TextField>
      ))}
      <Box {...oSAgreeBoxProps}>
        <InputBase {...oSCheckboxProps} />
        <Typography {...oSAgreeTypoProps} sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate('/terms-and-condition')}>
          {agreeInfo}
        </Typography>
      </Box>

      <Box {...oSAllowInfoBoxProps}>
        <InfoIcon {...oSAllowInfoIconProps} />
        <Typography {...oSAllowInfoTypoProps}>{allowInfo}</Typography>
      </Box>
    </>
  );

  const oSProceedProgressProps = {
    color: 'warning',
    size: 20,
  };

  const oSProceedProps = {
    children: 'Proceed',
    disabled: loading,
    onClick: undefined,
    startIcon: loading && <CircularProgress {...oSProceedProgressProps} />,
    className: `${orgSetupClasses?.proceedBtn} ${currDarkThemeClass}`,
    type: 'submit',
  };

  const downloadMsgProps = {
    currDarkThemeClass,
  };

  return (
    <OrgOutlinePage {...oSOrgOutlineProps}>
      <Box {...oSBoxProps}>
        {loading || values?.downloading ? (
          <DownloadingMetrcDataMsg {...downloadMsgProps} />
        ) : (
          <>
            <Typography {...oSStepTypoProps}>
              STEP {values?.step ?? '#'}/{totalStepCount} COMPLETED
            </Typography>

            <Box {...oSProgressBoxProps}>{progressList}</Box>

            <Typography {...oSStepDetailTypoProps}>
              Step {values?.step ?? '#'}: {values?.stepDetail ?? 'Unknown'}
            </Typography>

            <Typography {...oSActionTypoProps}>{values?.title ?? 'Unknown'}</Typography>

            <Box {...oSFormBoxProps}>
              {+values?.step === 1 ? OrganizationForm : +values?.step === 2 ? ComplianceForm : <></>}
              <Button {...oSProceedProps} />
            </Box>
          </>
        )}
      </Box>
    </OrgOutlinePage>
  );
}
